import React, { useMemo } from 'react';
import EmployeeAvatar from './EmployeeAvatar';
import SlackLink from './SlackLink';
import { useLocale, useT } from '../contexts/LocaleProvider';
import { deptLabel } from '../lib/departments';

function buildTree(employees) {
  const byEmail = new Map(employees.map(e => [e.email.toLowerCase(), e]));
  const childrenOf = new Map();
  const roots = [];
  for (const e of employees) {
    const me = (e.manager_email || '').toLowerCase();
    const hasParent = me && byEmail.has(me) && me !== e.email.toLowerCase();
    if (hasParent) {
      if (!childrenOf.has(me)) childrenOf.set(me, []);
      childrenOf.get(me).push(e);
    } else {
      roots.push(e);
    }
  }
  const byName = (a, b) => (a.full_name || '').localeCompare(b.full_name || '');
  for (const kids of childrenOf.values()) kids.sort(byName);
  roots.sort(byName);
  return { roots, childrenOf };
}

function Node({ employee, childrenOf, visited, onEdit, locale }) {
  const key = employee.email.toLowerCase();
  if (visited.has(key)) return null;  // cycle defence
  const seen = new Set(visited);
  seen.add(key);
  const kids = childrenOf.get(key) || [];
  return (
    <li>
      <div className="relative">
        <button
          type="button"
          className="org-card"
          onClick={onEdit ? () => onEdit(employee) : undefined}
          disabled={!onEdit}
        >
          <EmployeeAvatar fullName={employee.full_name} email={employee.email} photo={employee.photo} size={44} />
          <div className="org-card-name">{employee.full_name}</div>
          {employee.poste && <div className="org-card-role">{employee.poste}</div>}
          {employee.departement && <div className="org-card-dept">{deptLabel(employee.departement, locale)}</div>}
        </button>
        <SlackLink
          slackUserId={employee.slack_user_id}
          size={13}
          className="absolute top-1.5 right-1.5 p-1 rounded hover:bg-black/[0.06]"
        />
      </div>
      {kids.length > 0 && (
        <ul>
          {kids.map(k => (
            <Node key={k.id} employee={k} childrenOf={childrenOf} visited={seen} onEdit={onEdit} locale={locale} />
          ))}
        </ul>
      )}
    </li>
  );
}

export default function OrgChart({ employees, onEdit }) {
  const t = useT();
  const { locale } = useLocale();
  const { roots, childrenOf } = useMemo(() => buildTree(employees), [employees]);
  if (!employees.length) {
    return (
      <div className="bg-[hsl(var(--surface))] border border-border rounded-lg p-10 text-center text-sm text-[hsl(var(--text-soft))]">
        {t('common.empty')}
      </div>
    );
  }
  return (
    <div className="bg-[hsl(var(--surface))] border border-border rounded-lg">
      <div className="org-scroll">
        <div className="org-tree">
          <ul>
            {roots.map(r => (
              <Node key={r.id} employee={r} childrenOf={childrenOf} visited={new Set()} onEdit={onEdit} locale={locale} />
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
